<!-- This component is used for Non FTC Registered Purchase, Ex-sub purchase, Switch Plan, INTL Edit Payment -->
<template id="payment">
    <section>
        <form
            class="padded-container payment-layout__container"
            ref="paymentForm"
            v-on:submit.stop.prevent>
            <div v-if="!isEditPayment" class="payment-layout__step-text">
                <h1 class="payment-layout__step-title">
                    {{ getStepTitle }}
                </h1>
                <div class="payment-layout__step-subtitle">
                    {{ getSubHeading }}
                </div>
            </div>
            <div
                class="aa-layout"
                v-bind:class="[
                    isEditPayment ? 'payment-layout--edit account-grid' : 'payment-layout',
                    { 'payment-summary__layout': enhancedPaymentSummaryEnabled },
                    { '-no-steps-header': !showStepsHeader },
                ]"
                :aa-region="formConfig.aaRegion">
                <steps-header
                    v-if="showStepsHeader"
                    :class="[
                        'payment-layout__steps-header qt-payment-header',
                        { '-steps-header-full-width': enhancedPaymentSummaryEnabled },
                    ]" />

                <div
                    v-if="isEditPayment && !isFromPaymentSummary"
                    class="grid-item grid-item--payment-row grid-item--row-bordered sub-page--header aa-layout">
                    <h3>
                        {{ pageAttribute.get('ap_page_title', $getLocale('account_payment')) }}
                    </h3>
                </div>

                <!-- tabs menu -->
                <div
                    :class="['payment-layout__tabs', { '-no-steps-header': !showStepsHeader }]"
                    role="tablist"
                    aria-orientation="horizontal">
                    <template v-if="featurePaypal && featureFlagPaypal">
                        <a
                            class="tab qt-cctab"
                            role="tab"
                            id="tab-cards"
                            :aria-selected="activeTab === tab.cc ? 'true' : 'false'"
                            aria-controls="panel-cards"
                            tabindex="1"
                            @keydown.enter.prevent="handleEnterKey"
                            @click="handleClickTab($event, tab.cc)"
                            aria-label="Credit Card">
                            <CreditCardLogos :cards="this.acceptedCards" />
                        </a>
                        <a
                            class="tab tab--payPal qt-paypaltab"
                            role="tab"
                            id="tab-paypal"
                            :aria-selected="activeTab === tab.payPal ? 'true' : 'false'"
                            aria-controls="panel-paypal"
                            tabindex="1"
                            @keydown.enter.prevent="handleEnterKey"
                            @click="handleClickTab($event, tab.payPal)"
                            aria-label="PayPal"></a>
                        <div :class="tabIndicatorClass"></div>
                    </template>
                    <template v-else>
                        <div
                            class="tab qt-cctab"
                            role="tab"
                            id="tab-cards"
                            :aria-selected="'true'"
                            aria-controls="panel-cards"
                            tabindex="1"
                            aria-label="Credit Card">
                            <CreditCardLogos :cards="this.acceptedCards" />
                        </div>
                        <div :class="tabIndicatorClass"></div>
                    </template>
                </div>

                <!-- START OF AB TEST PLOCTOPLUS-1915: center alignment payment page -->
                <!-- payment form tab (credit card/paypal): no AB Test, center alignment payment page AB TEST, control, variants A and B -->
                <transition-group
                    name="fade"
                    tag="div"
                    :class="[
                        { ...getPaymentFormTabsModuleClass },
                        { '-no-steps-header': !showStepsHeader },
                    ]">
                    <div
                        v-show="activeTab === tab.cc"
                        :key="tab.cc"
                        id="panel-cards"
                        role="tabpanel"
                        aria-labelledby="tab-cards"
                        :class="{ 'payment-layout__tab-content__cc': true }">
                        <div v-if="formErrorMessage" role="alert" class="form-message error">
                            <p v-if="formErrorMessage">
                                {{ formErrorMessage }}
                            </p>
                        </div>

                        <!-- START AB TEST PLOCTOPLUS-2033 : Added @recurlyFocus -->
                        <FormFields
                            v-show="activeTab === tab.cc"
                            :key="tab.cc"
                            class="form-cc-payment"
                            v-bind:class="[
                                registrationCountry ? countryFormClass : '',
                                { long: altPaymentFormView },
                            ]"
                            :formFieldList="formFieldList"
                            :fieldErrors="fieldErrors"
                            @updateFormData="updateFormData"
                            @validateField="validateField"
                            @validateForm="validateForm"
                            @recurlyFocus="handleRecurlyFocus"></FormFields>
                        <!-- END AB TEST PLOCTOPLUS-2033 : Added @recurlyFocus -->
                    </div>

                    <div
                        v-show="activeTab === tab.payPal"
                        :key="tab.payPal"
                        id="panel-paypal"
                        role="tabpanel"
                        aria-labelledby="tab-paypal"
                        class="payment-layout__tab-content__paypal">
                        <transition-group
                            name="fade"
                            tag="div"
                            class="paypal-message qt-paypalcopytxt">
                            <p
                                v-if="payPalToken"
                                key="paypal-success"
                                class="paypal-message__heading paypal-message__success-heading">
                                {{ paypalSuccessMessage }}
                            </p>

                            <div v-else key="paypal-instruction">
                                <p class="paypal-message__heading">
                                    {{
                                        $getLocale(
                                            'you_will_be_directed_to_the_paypal_website_to_complete_your_payment',
                                        )
                                    }}
                                </p>

                                <p class="paypal-message__body">
                                    {{
                                        $getLocale(
                                            'this_will_be_saved_as_the_default_payment_method_for_your_subscription._You_can_change_your_payment_method_at_any_time_by_visiting_your_account_settings',
                                        )
                                    }}
                                </p>
                            </div>
                        </transition-group>
                    </div>
                </transition-group>

                <!-- order summary and agreement copy: PLOCTOPLUS-1915 center alignment payment page AB TEST, variant A and no AB Test -->
                <div
                    v-if="!isEditPayment && !isCenterAlignPaymentPageModulesAbove"
                    :class="[
                        'order-summary-wrapper',
                        'payment-layout__summary',
                        { '-no-steps-header': !this.showStepsHeader },
                    ]">
                    <OrderSummary
                        @found-price="updatePrice"
                        :coupon="coupon"
                        :couponCheckPending="couponCheckPending"
                        :couponDisclaimer="couponDisclaimerCopy"
                        @applyCoupon="applyCoupon"
                        @updateOrder="updateOrder"
                        @resetCoupon="resetCoupon"
                        @trialEndDateFormatted="updateTrialEndDate"
                        ref="orderSummary"
                        :recurly="recurly"
                        :annualPlanOption="annualPlanOption"
                        :formConfig="orderSummaryConfig"
                        :isFromDirect="isFromDirect"
                        v-if="!enhancedPaymentSummaryEnabled"></OrderSummary>
                    <BillingAgreementSummary
                        @found-price="updatePrice"
                        :coupon="coupon"
                        :couponCheckPending="couponCheckPending"
                        :couponDisclaimer="couponDisclaimerCopy"
                        @applyCoupon="applyCoupon"
                        @updateOrder="updateOrder"
                        @resetCoupon="resetCoupon"
                        @trialEndDateFormatted="updateTrialEndDate"
                        ref="orderSummary"
                        :recurly="recurly"
                        :annualPlanOption="annualPlanOption"
                        :formConfig="orderSummaryConfig"
                        :promo="this.appliedCouponCode"
                        :pricing="pricing"
                        :pageAttr="pageAttr"
                        :couponDiscountBillingDate="getCouponDiscountBillingDate"
                        :nextNonZeroBillingPrice="nextNonZeroBillingPrice"
                        :isFromDirect="isFromDirect"
                        v-if="enhancedPaymentSummaryEnabled"></BillingAgreementSummary>
                    <p
                        v-if="!isEditPayment && displayStartCBSButton"
                        v-html="getAgreementCopy()"
                        class="payment-layout__summary__agreement"></p>

                    <input type="hidden" name="recurly-token" data-recurly="token" />

                    <template v-if="needsWaiveOfCoolingRights && displayStartCBSButton">
                        <CheckboxElm
                            data-testid="waiveOfCoolingRighsCheckbox"
                            :checkboxConfig="waiveOfCoolingRighsCheckboxConfigs"
                            :errorMessage="waiveOfCoolingRighsCheckboxErrorMessage"
                            @change="setWaiveOfCoolingRights($event)"></CheckboxElm>
                        <div
                            data-testid="waiveOfCoolingRighsCheckboxError"
                            role="alert"
                            aria-live="polite"
                            class="text-input-error"
                            v-if="waiveOfCoolingRighsCheckboxErrorMessage">
                            {{ waiveOfCoolingRighsCheckboxErrorMessage }}
                        </div>
                    </template>

                    <FormButton
                        v-if="displayStartCBSButton"
                        :buttonConfig="buttonConfig"
                        :buttonState="submitButtonState"
                        @click="onClickSubmit"></FormButton>
                    <button
                        v-else
                        class="payment-layout__summary__submit paypal-checkout qt-paypalbtn"
                        @click="handlePayPalCheckout($event, paypalGateway)">
                        <span class="label">{{ $getLocale('checkout_with') }}</span>
                        <span class="logo" aria-label="PayPal"></span>
                    </button>

                    <div class="payment-layout__status" role="status" aria-live="polite">
                        {{ submitStatus }}
                    </div>
                </div>

                <!-- order summary: PLOCTOPLUS-1915 center alignment payment page AB TEST, variant B -->
                <div
                    v-if="!isEditPayment && isCenterAlignPaymentPageModulesAbove"
                    class="order-summary-wrapper payment-layout__summary order-summary-module">
                    <OrderSummary
                        @found-price="updatePrice"
                        :coupon="coupon"
                        :couponCheckPending="couponCheckPending"
                        :couponDisclaimer="couponDisclaimerCopy"
                        @applyCoupon="applyCoupon"
                        @updateOrder="updateOrder"
                        @resetCoupon="resetCoupon"
                        ref="orderSummary"
                        :recurly="recurly"
                        :annualPlanOption="annualPlanOption"
                        :isFromDirect="isFromDirect"
                        :formConfig="orderSummaryConfig"></OrderSummary>
                    <BillingAgreementSummary
                        @found-price="updatePrice"
                        :coupon="coupon"
                        :couponCheckPending="couponCheckPending"
                        :couponDisclaimer="couponDisclaimerCopy"
                        @applyCoupon="applyCoupon"
                        @updateOrder="updateOrder"
                        @trialEndDateFormatted="updateTrialEndDate"
                        ref="orderSummary"
                        :recurly="recurly"
                        :annualPlanOption="annualPlanOption"
                        :formConfig="orderSummaryConfig"
                        :promo="this.appliedCouponCode"
                        :isFromDirect="isFromDirect"
                        :couponDiscountBillingDate="getCouponDiscountBillingDate"
                        v-if="enhancedPaymentSummaryEnabled"></BillingAgreementSummary>
                </div>

                <!-- agreement copy: PLOCTOPLUS-1915 center alignment payment page AB TEST, variant B  -->
                <div
                    v-if="!isEditPayment && isCenterAlignPaymentPageModulesAbove"
                    class="order-summary-wrapper payment-layout__summary agreement-copy-module">
                    <p
                        v-if="displayStartCBSButton"
                        v-html="getAgreementCopy()"
                        class="payment-layout__summary__agreement"></p>

                    <input type="hidden" name="recurly-token" data-recurly="token" />

                    <template v-if="needsWaiveOfCoolingRights && displayStartCBSButton">
                        <CheckboxElm
                            data-testid="waiveOfCoolingRighsCheckbox"
                            :checkboxConfig="waiveOfCoolingRighsCheckboxConfigs"
                            :errorMessage="waiveOfCoolingRighsCheckboxErrorMessage"
                            @change="setWaiveOfCoolingRights($event)"></CheckboxElm>
                        <div
                            data-testid="waiveOfCoolingRighsCheckboxError"
                            role="alert"
                            aria-live="polite"
                            class="text-input-error"
                            v-if="waiveOfCoolingRighsCheckboxErrorMessage">
                            {{ waiveOfCoolingRighsCheckboxErrorMessage }}
                        </div>
                    </template>

                    <FormButton
                        v-if="displayStartCBSButton"
                        :buttonConfig="buttonConfig"
                        :buttonState="submitButtonState"
                        @click="onClickSubmit"></FormButton>
                    <button
                        v-else
                        class="payment-layout__summary__submit paypal-checkout qt-paypalbtn"
                        @click="handlePayPalCheckout($event, paypalGateway)">
                        <span class="label">{{ $getLocale('checkout_with') }}</span>
                        <span class="logo" aria-label="PayPal"></span>
                    </button>

                    <div class="payment-layout__status" role="status" aria-live="polite">
                        {{ submitStatus }}
                    </div>
                </div>
                <!-- END OF AB TEST PLOCTOPLUS-1915: center alignment payment page -->

                <!-- edit payment cancel button -->
                <div
                    v-if="isEditPayment"
                    class="grid-item grid-item--row sub-page--actionbar sub-page--actionbar-padded">
                    <template v-if="activeTab === tab.cc">
                        <FormButton
                            v-if="!isFromPaymentSummary"
                            :buttonConfig="cancelConfig"
                            @click="cancelOnClicked"></FormButton>
                        <FormButton
                            :buttonConfig="buttonConfig"
                            :buttonState="submitButtonState"
                            @click="onClickSubmit"></FormButton>
                    </template>
                    <template v-else-if="activeTab === tab.payPal">
                        <ButtonCta
                            v-if="!isFromPaymentSummary"
                            :buttonConfig="cancelConfig"></ButtonCta>
                        <button
                            class="account-payment-layout__summary__submit paypal-checkout qt-paypalbtn"
                            @click="handlePayPalCheckout($event, paypalGateway)">
                            <span class="paypal-label">
                                {{ $getLocale('switch_to') }}
                            </span>
                            <span class="logo" aria-label="PayPal"></span>
                        </button>
                    </template>
                </div>
            </div>
        </form>
        <transition name="fade">
            <WaitingOverlay
                :line1="getPaymentProcessingMessage"
                :line2="$getLocale('this_will_not_take_long')"
                v-if="waitingOverlayEnabled && formIsProcessing" />
        </transition>
        <div class="threeDSecureModal" v-bind:class="{ open: threeDModalOpen }">
            <div class="backdrop"></div>
            <div class="container" ref="threeDSecure"></div>
        </div>
    </section>
</template>

<script>
    import ButtonCta from 'atoms/ButtonCta'; // ~/src/web-package-coreui/src/js/atoms/ButtonCta.vue
    import CheckboxElm from 'atoms/CheckboxElm'; // ~/src/web-package-coreui/src/js/atoms/CheckboxElm.vue
    import CreditCardLogos from 'aa/vue/components/CreditCardLogos'; // ~/src/web-allaccess/src/js/vue/components/CreditCardLogos.vue
    import CustomSelect from 'atoms/CustomSelect'; // ~/src/web-package-coreui/src/js/atoms/CustomSelect.vue
    import FormButton from 'atoms/FormButton'; // ~/src/web-package-coreui/src/js/atoms/FormButton.vue
    import FormFields from 'atoms/FormFields';
    import FormState from 'aa/vue/plugins/FormState';
    import InputElm from 'atoms/InputElm';
    import LabelInput from 'atoms/LabelInput';
    import NotificationBarModel from 'aa/vue/models/NotificationBarModel';
    import OrderSummary from 'aa/vue/components/OrderSummary';
    import RecurlyHostedField from 'atoms/RecurlyHostedField';
    import StepsHeader from 'aa/vue/components/StepsHeader';
    import WaitingOverlay from 'aa/vue/atoms/WaitingOverlay';
    import modalMixin from 'aa/vue/plugins/modalMixin';
    import { CreditCardPaymentConfig, OrderSummaryConfig, TAB } from 'aa/vue/FormConfig';
    import { mapActions, mapGetters, mapState } from 'vuex';
    import { ACTION_NAMES, PAGE_TYPES, tracking } from 'services/Tracking';
    import { PAGE_TYPES as AA_PAGE_TYPES } from 'aa/vue/constants/aaTrackingConstants';
    import commonMixins from 'aa/vue/plugins/account/commonMixins';
    import couponsMixin from 'aa/vue/plugins/couponsMixin';
    import BaseModalWrapper from 'aa/vue/atoms/BaseModalWrapper';
    import pricePrettyPrint from 'aa/vue/plugins/priceMixin';
    import { DateFormatter, getEndDate } from 'aa/helpers/DateFormatter';
    import { sprintf } from 'sprintf-js';
    import moment from 'moment';
    import { FLOW_TYPE } from 'models/FlowModel';
    import Domain from 'cbs-core-ui/src/js/helpers/Domain';
    import {
        SKIP_EXPLAINER_STEPS,
        FTC_COMPLIANCE_ENABLED,
        MULTI_SUB_PLAN_PICKER_ENABLED,
    } from 'aa/helpers/featureConstants';
    import { Tooltip, TooltipConfig } from 'components/Tooltip';
    import BillingAgreementSummary from 'aa/vue/components/BillingAgreementSummary';

    // START TEST PLOCTOPLUS-1915: center align payment page
    import CenterAlignPaymentPageAbTest from 'aa/vue/abTestRelated/CenterAlignPaymentPageAbTest';
    // END TEST PLOCTOPLUS-1915: center align payment page
    import checkoutMixin from 'aa/vue/plugins/checkoutMixin';
    import aarpMixin from 'aa/vue/plugins/aarpMixin';
    import { pluralizeDuration } from 'aa/helpers/stringManipulation';

    import billingInfoMixin from '../account/billingInfoMixin';
    import { RECAPTCHA_FORMS } from 'recaptcha/Recaptcha';
    export default {
        name: 'SharedPaymentComponent',
        mixins: [
            FormState,
            modalMixin,
            couponsMixin,
            commonMixins,
            pricePrettyPrint,
            // START TEST PLOCTOPLUS-1915: center align payment page
            CenterAlignPaymentPageAbTest,
            // END TEST PLOCTOPLUS-1915: center align payment page
            checkoutMixin,
            aarpMixin,
            billingInfoMixin,
        ],
        components: {
            BaseModalWrapper,
            FormButton,
            FormFields,
            StepsHeader,
            InputElm,
            LabelInput,
            CustomSelect,
            CheckboxElm,
            CreditCardLogos,
            RecurlyHostedField,
            OrderSummary,
            WaitingOverlay,
            ButtonCta,
            BillingAgreementSummary,
        },
        props: {
            isEditPayment: {
                type: Boolean,
                default: false,
            },
            handleProcessedPayment: {
                type: Function,
                default: () => {},
            },
            handleProcessedPaymentError: {
                type: Function,
                default: () => {},
            },
            waitingOverlayEnabled: {
                type: Boolean,
                default: true,
            },
            isFromDirect: {
                type: Boolean,
                default: false,
            },
            isFromPaymentSummary: {
                type: Boolean,
                default: false,
            },
            errorMessageElementId: {
                type: String,
                default: null,
            },
            pageTypeForTracking: {
                type: String,
                default: '',
            },
        },
        data: function () {
            return {
                checkboxConfig: {
                    styleClass: 'cbs-checkbox-input',
                },
                activeTab: TAB.cc,
                annualPlanOption: false,
                enhancedPaymentSummaryEnabled: false,
                payPalToken: null,
                acceptedCards: [],
                countryFormClass: '',
                altPaymentFormView: false,
                paymentRequestData: {},
                bodyScrollPos: 0,
                submitStatus: '',
                orderSummaryConfig: OrderSummaryConfig,

                nflBannerFlag: 'dsp_nfl_game_pass',
                clickEvent: null,
                // START TEST PLOCTOPLUS-2033
                tooltip: null,
                // END TEST PLOCTOPLUS-2033
                couponDiscountBillingDate: null,
                recaptchaAction: RECAPTCHA_FORMS.FORM_PAYMENT,
                editPaymentSuccess: false,
            };
        },
        computed: {
            ...mapState('user', {
                isExSubscriber: (state) => state.isExSubscriber,
                isSubscriber: (state) => state.isSubscriber,
            }),
            ...mapState('payment', {
                formReference: (state) => state.formReference,
            }),
            ...mapState({
                promo: (state) => state.promo,
            }),
            ...mapState('authSuite', {
                accessStatus: (state) => state.accessStatus,
            }),
            ...mapGetters('flow', {
                flowType: 'type',
                isNoFreeTrialCampaign: 'isNoFreeTrialCampaign',
                isCampaign: 'isCampaign',
                isGiftFlow: 'isGift',
                isEdu: 'isEdu',
            }),
            ...mapGetters({
                recaptchaFormActionEnabled: 'featureFlags/recaptchaFormActionEnabled',
                selectedPlan: 'plan/getSelectedPlan',
            }),
            ...mapGetters('user', ['isRegistered']),

            isDomestic() {
                return Domain.isDomestic();
            },
            paypalSuccessMessage() {
                return this.isDomestic
                    ? this.pageAttr.aa_payment_paypal_success_copy
                    : this.pageAttr.billing_summary_paypal_success_copy;
            },
            getPaymentProcessingMessage() {
                return this.isEditPayment
                    ? this.$getLocale('we_are_updating_your_payment_method')
                    : this.$getLocale('we_are_processing_your_subscription');
            },
            hasSkipExplainers() {
                return this.featureIsActive(SKIP_EXPLAINER_STEPS);
            },
            getSubHeading() {
                if (this.hasSkipExplainers) {
                    return this.getSkipExplainersSubHeading;
                } else {
                    if (this.isDomestic) {
                        return this.getDomesticSubHeading;
                    } else {
                        return this.getIntlSubHeading;
                    }
                }
            },
            getPlanTrialEndDateFormatted() {
                if (!this.selectedPlan.trialEndDate) {
                    return '';
                }

                return moment(this.selectedPlan.trialEndDate).format('MMMM Do YYYY');
            },
            getSkipExplainersSubHeading() {
                let copy =
                    this.isExSubscriber || this.coupon.hasOwnProperty('recurlyInfo')
                        ? this.pageAttr.aa_page_sub_header_exsub
                        : this.pageAttr.aa_page_sub_header;
                let formattedDate = '';
                if (this.trialEndDateFormatted) {
                    formattedDate = moment(this.trialEndDateFormatted).format('MMMM Do YYYY');
                } else {
                    formattedDate = moment(Date.now()).add('7', 'days').format('MMMM Do YYYY');
                }
                return copy.indexOf('%s') >= 0 ? sprintf(copy, formattedDate) : copy;
            },
            getDomesticSubHeading(pageAttrKey) {
                // TODO: remove bundle logic, or delete entirely, or something in between
                let formattedDate = this.getPlanTrialEndDateFormatted,
                    validCoupon =
                        this.appliedCouponCode &&
                        !this.coupon.errorMessage &&
                        !this.couponCheckPending,
                    onlyShowGeneric = this.isGenericCampaign || this.isExSubscriber;

                if (onlyShowGeneric && this.bundleShowtime) {
                    // show showtime generic no matter what
                    return this.pageAttr.explainer_subtitle_generic_showtime;
                } else if (onlyShowGeneric) {
                    //  show generic no matter what
                    return this.pageAttr.explainer_subtitle_generic;
                } else if (validCoupon) {
                    // if valid coupon show correct generic
                    if (this.bundleShowtime) {
                        return this.pageAttr.explainer_subtitle_generic_showtime;
                    } else {
                        return this.pageAttr.explainer_subtitle_generic;
                    }
                } else if (this.bundleShowtime) {
                    // show bundle showtime
                    return sprintf(this.pageAttr.explainer_subtitle_showtime, formattedDate);
                } else {
                    // show normal trial copy
                    return sprintf(this.pageAttr.explainer_subtitle, formattedDate);
                }
            },

            getIntlSubHeading() {
                const { duration, period } = this.getDurationAndPeriod();
                let date = getEndDate(duration, period);

                // Return if coupon is 100% off and new Payment summary is enabled
                if (
                    this.enhancedPaymentSummaryEnabled &&
                    this.coupon?.recurlyInfo?.discount?.type === 'percent' &&
                    this.coupon?.recurlyInfo?.discount?.rate === 1
                ) {
                    this.updateFirstBillingDate(this.coupon);
                    return '';
                }

                const isAre = duration === 1 ? 'is' : 'are';

                date = DateFormatter.format(
                    date,
                    {
                        month: 'long',
                    },
                    CBS.Registry.region.dateLocale,
                );

                // return `Your first ${duration} ${period} ${isAre} free, and you won’t be charged until {DATE}. Cancel anytime.`
                //.replace("{DATE}", date)
                if (this.coupon?.trialString || this.selectedPlan?.trialString) {
                    let explainerLine1 = '';
                    if (this.pageAttr.explainer_line_1) {
                        explainerLine1 = this.pageAttr.explainer_line_1
                            ?.replace('{duration}', duration)
                            ?.replace('{period}', this.$getLocale(period))
                            ?.replace('{isAre}', isAre)
                            ?.replace('{date}', date);

                        // Support legacy format while the CMS still has %s for the date in this string
                        if (explainerLine1.indexOf('%s') >= 0) {
                            return sprintf(explainerLine1, date);
                        }
                    }
                } else {
                    const dateStr = moment(new Date()).add('7', 'days').format('MM/DD/YYYY');
                    return this.pageAttr.aa_page_sub_header.replace('{DATE}', dateStr);
                }
            },

            paypalGateway() {
                if (this.featurePaypal) {
                    if (this.$store.state.serverData?.appMode) {
                        return this.$store.state.serverData.paymentMethods.paypal?.gateway?.default
                            ? this.$store.state.serverData.paymentMethods.paypal?.gateway.default[
                                  this.$store.state.serverData?.appMode
                              ]
                            : false;
                    }
                    return false;
                }
                return false;
            },
            showStepsHeader() {
                if (this.$store.state.serverData.hideStepsHeader) {
                    return false;
                }

                return !this.isEditPayment && !this.hasSkipExplainers;
            },
            getStepTitle() {
                if (this.hasSkipExplainers) {
                    return this.pageAttr.aa_page_header;
                } else {
                    return this.pageAttr.explainer_description;
                }
            },

            displayStartCBSButton() {
                return this.activeTab === TAB.cc || this.payPalToken;
            },

            formConfig() {
                return CreditCardPaymentConfig;
            },

            cancelConfig() {
                return {
                    type: 'button',
                    styleClass: ['button', 'secondary'],
                    displayText: this.$getLocale('cancel'),
                    event: this.cancelOnClicked,
                };
            },

            tabIndicatorClass() {
                return [
                    'active-indicator',
                    {
                        'active-indicator--payPal': this.activeTab === TAB.payPal,
                    },
                    { '-no-steps-header': !this.showStepsHeader },
                ];
            },

            isFTCComplianceEnabled() {
                return this.featureIsActive(FTC_COMPLIANCE_ENABLED);
            },

            tab() {
                return TAB;
            },

            isGenericCampaign() {
                return this.pageAttr.use_generic_campaign_explainer_set;
            },

            language() {
                return this.isEditPayment ? '' : this.$store.state.serverData.language;
            },

            getCouponDiscountBillingDate() {
                return this.couponDiscountBillingDate;
            },
            trackingService() {
                return this.$trackingService ?? tracking;
            },
            isEditPaymentRoute() {
                return location.pathname.includes('/account/payment');
            },
            trackingPageType() {
                if (this.pageTypeForTracking) {
                    return this.pageTypeForTracking;
                } else {
                    return this.isEditPayment
                        ? this.isEditPaymentRoute
                            ? PAGE_TYPES.ACCOUNT_UPDATE_PAYMENT
                            : AA_PAGE_TYPES.ACCOUNT_SWITCH_PAYMENT
                        : this.isExSubscriber
                          ? AA_PAGE_TYPES.EXSUB_PAYMENT
                          : AA_PAGE_TYPES.PAYMENT;
                }
            },
        },

        methods: {
            ...mapActions('payment', ['updateShortCode']),
            // START TEST PLOCTOPLUS-2033
            handleRecurlyFocus() {
                if (this.tooltip) {
                    this.tooltip.hide(new Event('click'));
                }
            },
            // END TEST PLOCTOPLUS-2033

            getFieldLabel: function (fieldName) {
                // TODO: move to mixin SubFlowForm
                if (this.pageAttr[this.formName + '_' + fieldName.toLowerCase()]) {
                    return this.pageAttr[this.formName + '_' + fieldName.toLowerCase()];
                }
            },

            getDurationAndPeriod() {
                let duration = 7;
                let period = 'days';

                if (this.coupon?.recurlyInfo?.discount?.trial) {
                    const { amount, unit } = this.coupon.recurlyInfo.discount.trial;
                    duration = amount;
                    period = unit;
                    period = pluralizeDuration(duration, period);
                }

                return { duration, period };
            },

            handleEnterKey(e) {
                e.target.click();
            },

            handleClickTab(e, tabId) {
                e.preventDefault();
                if (tabId === TAB.cc) {
                    this.activeTab = TAB.cc;
                } else {
                    this.activeTab = TAB.payPal;
                }

                const trackingParams = {
                    ctaText: this.activeTab,
                    pageType: this.trackingPageType,
                };

                this.trackingService.trackAction(
                    ACTION_NAMES.PAYMENT_METHOD_SELECTED,
                    trackingParams,
                );
            },

            cancelOnClicked() {
                tracking.trackAction(ACTION_NAMES.CREDIT_CARD_CANCEL);
                this.$router.push({ name: 'AccountMain' });
            },

            resubmitPayment(token) {
                const payload = {
                    ...this.paymentRequestData,
                    resultActionTokenId: token.id,
                };
                const httpData = this.isEditPayment ? this.buildHttpData(payload) : payload;
                this.sendPayment(httpData);
            },

            submitPayment(token, buttonEvent) {
                this.formErrorMessage = null;
                this.formIsProcessing = true;

                let couponCode = null;
                if (this.coupon && this.coupon.recurlyInfo && this.coupon.recurlyInfo.code) {
                    couponCode = this.coupon.recurlyInfo.code;
                }

                let payload = {
                    token: token,
                    buttonEvent: buttonEvent,
                    plan: this.selectedPlan,
                    couponCode: couponCode,
                    tk_trp: this.$store.state.token,
                };

                if (this.flowType === FLOW_TYPE.AARP) {
                    let aarpUserId = { aarpUserId: this.getAarpUserId() };
                    Object.assign(payload, aarpUserId);
                }

                // Save payload for retry if needed
                this.paymentRequestData = payload;

                this.isEditPayment && tracking.trackAction(ACTION_NAMES.CREDIT_CARD_SUBMIT_UPDATE);
                this.sendPayment(payload);
            },

            sendPayment(payload) {
                this.formIsProcessing = true;

                if (
                    this.hasOwnProperty('recaptchaAction') &&
                    this.recaptchaFormActionEnabled(this.recaptchaAction)
                ) {
                    payload.recaptchaAction = this.recaptchaAction;
                }

                const couponCode = payload.couponCode;
                if ('URLSearchParams' in window) {
                    if (location.search) {
                        const searchParams = new URLSearchParams(location.search.substr(1));
                        const gatewayOverride = searchParams.get('gateway');
                        if (gatewayOverride) {
                            payload.gateway = gatewayOverride;
                        }
                    }
                }

                const processSelectedMethod = this.isEditPayment
                    ? this.processEditPayment
                    : this.processPayment;
                processSelectedMethod(payload)
                    .then(async (resp) => {
                        if (!this.isFromPaymentSummary) {
                            this.handleProcessedPayment(resp);
                        }

                        if (!resp.success || !this.isEditPayment) {
                            return;
                        }

                        if (this.isRegistered) {
                            this.$store.dispatch('storedPaymentMethod/updateBillingInfo');
                        }

                        const purchasePaymentMethod =
                            this.activeTab === this.tab.cc ? 'credit-card' : 'paypal';

                        if (this.isFromPaymentSummary) {
                            await this.syncServerDataWithDeepAssign();
                            this.formIsProcessing = false;
                            this.editPaymentSuccess = true;
                            this.trackingService.trackAction(
                                ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD_SUCCESS,
                                {
                                    ctaText: this.buttonConfig.displayText,
                                    pageURL: window.location.href,
                                    pageType: this.trackingPageType,
                                    purchasePaymentMethod,
                                },
                            );

                            // Timeout to allow the button success state to be displayed before closing the form in the payment summary page
                            setTimeout(() => {
                                this.$emit('update-payment-method-success', resp.success);
                                this.editPaymentSuccess = false;
                            }, 1000);

                            this.openNotification(
                                new NotificationBarModel({
                                    success: true,
                                    message: this.$getLocale(
                                        'your_payment_information_was_successfully_updated',
                                    ),
                                }),
                            );
                        }

                        if (!this.isFromPaymentSummary) {
                            this.trackingService.trackAction(
                                ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD_SUCCESS,
                                {
                                    linkName: ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD_SUCCESS,
                                    pageType: this.trackingPageType,
                                    pageURL: window.location.href,
                                    pageName: '/account/payment',
                                    purchasePaymentMethod,
                                    optimizelyExp: CBS.Tracking?.utag_data?.optimizelyExp,
                                    userType: this.$store.state.user?.svod?.status,
                                    userStatus: this.$store.getters['user/trackingUserStatus'],
                                    userProfileId: CBS.Registry?.user?.profile_id,
                                    userProfileCategory: CBS.Registry?.user?.profile_type,
                                    userProfilePicPath: CBS.Registry?.user?.userProfilePicPath,
                                    userRegId: this.$store.state?.user?.regID,
                                },
                            );
                        }
                    })
                    .catch((err) => {
                        let trackingParams = {};
                        if (this.activeTab === TAB.payPal) {
                            const { planType } = this.selectedPlan;
                            trackingParams.purchasePaymentMethod = this.activeTab;
                            trackingParams.productPricingPlan = planType;
                        }
                        trackingParams.pageType = this.trackingPageType;

                        this.handleProcessedPaymentError(err, trackingParams, this.isSubscriber);
                    })
                    .finally(() => {
                        if (this.formIsProcessing) {
                            this.formIsProcessing = false;
                        }
                    });
            },

            setUpThreeDSecure(actionTokenId) {
                if (actionTokenId) {
                    const risk = this.recurly.Risk();

                    const threeDSecure = risk.ThreeDSecure({ actionTokenId });
                    threeDSecure.on('error', (err) => {
                        this.formErrorMessage = this.$getLocale(
                            'recurly_PSD2_error_please_try_again',
                        );
                        this.closeThreeDModal();
                        threeDSecure.remove();
                    });
                    threeDSecure.on('token', (token) => {
                        this.resubmitPayment(token);
                        this.closeThreeDModal();
                        threeDSecure.remove();
                    });
                    this.openThreeDModal();
                    threeDSecure.attach(this.$refs.threeDSecure);
                }
            },

            CBSSportsUpdateShortCode() {
                if (this.$store.getters['flow/type'] === FLOW_TYPE.CBS_SPORTS) {
                    let payload = {
                        tk_trp: this.$store.state.token,
                        queryString: sessionStorage.getItem('oauth-cbssports-client-params'),
                    };
                    this.updateShortCode(payload)
                        .then((res) => {
                            // ignore response
                        })
                        .catch((err) => {
                            // ignore errors
                        })
                        .finally(() => {
                            sessionStorage.removeItem('oauth-cbssports-client-params');
                        });
                }
            },

            getDirectLinkPromoNotification(directLinkPromoPA, promoId) {
                const foundPromo = this.findPromoById(directLinkPromoPA, this.$route.query.msg);

                if (foundPromo) {
                    const notificationBarModel = new NotificationBarModel({
                        success: true,
                        autoClose: foundPromo.autoCloseTimer ?? null,
                        delayToClose: foundPromo.autoCloseTimer ?? null,
                        heading: foundPromo.promo_header ?? null,
                        message: this.getDirectLinkPromoCopy(foundPromo),
                        showInfoIcon: false,
                    });

                    return notificationBarModel;
                }
            },

            getDirectLinkPromoCopy(promo) {
                // if more than 1 copy present, combine them to be one string with multiple lines
                let promoCopy = '';

                for (let i = 1; i <= 2; i++) {
                    const copyKey = `promo_copy_${i}`;
                    if (promo[copyKey]) {
                        promoCopy += promo[copyKey] + '\n';
                    }
                }

                return promoCopy.trim();
            },

            findPromoById(directLinkPromoPA, promoId) {
                const promoKeyField = 'dl_promo_';
                // Extract keys starting with "DL_PROMO_"
                const promoKeys = Object.keys(directLinkPromoPA).filter((key) => {
                    if (!key.startsWith(promoKeyField)) {
                        return false;
                    }
                    const suffix = key.slice(promoKeyField.length);
                    const intSuffix = parseInt(suffix);
                    return !isNaN(intSuffix) && intSuffix > 0;
                });

                // Sort the keys based on their numeric suffix
                promoKeys.sort((a, b) => {
                    const numA = parseInt(a.replace(promoKeyField, ''), 10);
                    const numB = parseInt(b.replace(promoKeyField, ''), 10);
                    return numA - numB;
                });

                for (const key of promoKeys) {
                    try {
                        const promoData = JSON.parse(directLinkPromoPA[key]);
                        if (promoData['promo_id'] === promoId) {
                            return promoData;
                        }
                    } catch (error) {
                        console.error(`Error decoding JSON for key ${key}:`, error.message);
                    }
                }

                return null;
            },
        },

        // Lifecycle Hooks
        mounted() {
            this.enhancedPaymentSummaryEnabled =
                this.$store.state.serverData.enhancedPaymentSummaryEnabled;
            if (this.isEditPayment) {
                this.recaptchaAction = RECAPTCHA_FORMS.FORM_EDIT_PAYMENT;
            }

            this.$store.commit('payment/setFormReference', this.$refs.paymentForm);

            // START AB TEST PLOCTOPLUS-2033
            if (this.$store.getters['variant/isPaymentHypertextLink']) {
                this.tooltip = new Tooltip(
                    new TooltipConfig({
                        events: ['mouseover', 'click'],
                        selectorsList: ['.cc-hyperlink a', '.cc-checkmark-lock-icon'],
                        targetSelector: '.cc-checkmark-lock-icon',
                        sameWidthAsSelector: {
                            mobilePortrait: '.field-wrapper--cc-number',
                            mobileLandscape: '.field-wrapper--cc-number',
                            tabletPortrait: '.field-wrapper--cc-number',
                        },
                        innerHtmlTargetSelector: {
                            mobilePortrait: '.field-wrapper--cc-number',
                            mobileLandscape: '.field-wrapper--cc-number',
                            tabletPortrait: '.field-wrapper--cc-number',
                        },
                        innerHTML:
                            'We use SSL to encrypt your payment information while your payment is being processed. Paramount+ does not store your credit card information.',
                        trackOnShow: (event) => {
                            if (event && event.target) {
                                if (
                                    event.type === 'mouseover' &&
                                    event.target.id === 'lock-icon-hyperlink'
                                ) {
                                    tracking.trackAction('trackPaymentLearnMoreButtonHover');
                                } else if (
                                    event.type === 'mouseover' &&
                                    event.target.id === 'cc-lock-icon'
                                ) {
                                    tracking.trackAction('trackPaymentLockIconHover');
                                }
                            }
                        },
                    }),
                );
            }
            // END AB TEST PLOCTOPLUS-2033
            // needed as AbTestRunner is run after component is mounted so optimizelyExp is not set yet
            setTimeout(() => {
                if (this.isEditPayment && !this.isFromPaymentSummary) {
                    tracking.trackState({
                        pageType: this.trackingPageType,
                        optimizelyExp: CBS.Tracking.utag_data.optimizelyExp ?? '',
                        pageURL: window.location.href,
                    });
                }
            }, 500);
        },

        created() {
            this.countryFormClass = `form-${this.registrationCountry}`;
            this.altPaymentFormView = this.$store.state.serverData.altPaymentFormView;
            this.acceptedCards = this.$store.state.serverData.paymentMethods?.acceptedCreditCards;
            this.featurePaypal = this.$store.state.serverData.paymentMethods?.paypal.enabled;
            this.featureFlagPaypal = this.$store.state.serverData?.paypalAvailableInCountry;
            this.annualPlanOption = this.$store.state.serverData.annualPlanOption;

            let inputList = this.$store.state.serverData.paymentForm;

            let { recurlyFieldConfig } = CreditCardPaymentConfig;

            let fieldConfigs = {
                ...inputList,
                ...recurlyFieldConfig,
            };

            fieldConfigs.first_name.value = this.$store.getters['user/firstName'];
            fieldConfigs.last_name.value = this.$store.getters['user/lastName'];

            this.fieldConfigs = fieldConfigs;
            this.setValidationService(
                null,
                CBS?.Registry?.user?.userRegistrationCountry ?? '',
                this.$store.state.serverData.regionLocale ?? '',
            );

            this.subscriberFlag =
                (this.$store.getters['user/isExSubscriber'] ? 'Ex' : 'New') + ' Subscriber';

            if (this.isFromDirect && this.$route.params?.directLinkFlow) {
                const trackingParams = {
                    productPricingPlan: this.selectedPlan.planType,
                    pickPlanType: this.selectedPlan.planTier,
                    pickPlanSku: this.selectedPlan.recurlyCode,
                    productNewSku: this.selectedPlan.recurlyCode,
                    purchaseEventBillingStart: '1',
                    pageType: `winback_${this.$route.params.directLinkFlow}_directLink_payment`,
                    pageUrl: window.location.href,
                    purchaseProduct: this.selectedPlan.recurlyCode,
                    purchaseProductName: this.selectedPlan.titleForTracking,
                    purchasePrice: this.selectedPlan.rawPrice,
                    productOfferPeriod: this.selectedPlan?.trialString ?? '',
                    purchaseQuantity: '1',
                    purchasePromoCode: this.promo,
                };

                tracking.trackState(trackingParams);

                if (
                    !this.$store?.state?.serverData?.directLinkPageAttributes ||
                    !this.$route.query.msg
                ) {
                    return;
                }

                const notificationBarModel = this.getDirectLinkPromoNotification(
                    this.$store?.state?.serverData?.directLinkPageAttributes,
                    this.$route.query.msg,
                );

                if (notificationBarModel) {
                    this.openNotification(notificationBarModel);
                }

                if (this.coupon?.recurlyInfo) {
                    this.updateFirstBillingDate(this.coupon);
                }
            }
        },
        watch: {
            coupon(newVal) {
                this.couponDiscountBillingDate = '';
                this.updateFirstBillingDate(newVal);
            },
            plan(newVal) {
                if (this.coupon?.recurlyInfo) {
                    this.handleCouponChange(this.coupon.recurlyInfo, true);
                    this.updateFirstBillingDate(this.coupon);
                }
            },
            activeTab(newVal) {
                if (newVal === TAB.cc) {
                    this.$store.commit('payment/setFormReference', this.$refs.paymentForm);
                }
            },
        },
    };
</script>
